import { useGetFormTreatmentAttributesQuery } from '~/store/services/treatmentAttributes';
import { usePhaseStageCodesForModality } from '~/lib/hooks/useGetPhaseStagesForModality';
import {
  CarePlanInterventionResponse,
  CarePlanResponse,
} from '~/lib/types/carePlan/carePlans';
import { useGetProviderTypeManifestQuery } from '~/store/services/refCarePlanEnums';
import {
  unwrapRoleGroups,
  mapRoleCodesToLabels,
} from '~/lib/util/carePlan.util';

type PhaseStageCodeToInterventionListMap = Record<
  string,
  CarePlanInterventionResponse[]
>;

export const useInterventionsGroupedByPhaseStage = (
  treatmentEpisodeId: string,
  carePlan: CarePlanResponse,
  onlyIncludeRoles: string[] = [],
  includeCustomInterventions: boolean = false,
): {
  interventionsByPhaseStage: PhaseStageCodeToInterventionListMap;
  customInterventions: CarePlanInterventionResponse[];
  isLoading: boolean;
} => {
  const interventionsByPhaseStage: PhaseStageCodeToInterventionListMap = {};
  const customInterventions: CarePlanInterventionResponse[] = [];

  const { data: forms, isLoading: isGetFormTreatmentAttributesLoading } =
    useGetFormTreatmentAttributesQuery(treatmentEpisodeId, {
      skip: !carePlan.treatmentEpisodeModality,
    });

  const activeForm = forms?.find((form) => form.endDate === null);
  const { phaseStages, isLoading: isGetPhaseStagesLoading } =
    usePhaseStageCodesForModality(
      carePlan.treatmentEpisodeModality?.modality.code,
      activeForm?.form.code,
    );

  const {
    data: providerTypeManifest,
    isLoading: isGetProviderTypeManifestLoading,
  } = useGetProviderTypeManifestQuery();

  if (
    isGetPhaseStagesLoading ||
    isGetFormTreatmentAttributesLoading ||
    isGetProviderTypeManifestLoading
  ) {
    return { interventionsByPhaseStage, customInterventions, isLoading: true };
  }

  phaseStages.forEach((phaseStage) => {
    interventionsByPhaseStage[phaseStage.label] = [];
  });

  const getCombinedRoles = (
    intervention: CarePlanInterventionResponse,
  ): string[] => {
    // TODO NX-2562: Map role codes to labels for custom interventions only, remove once BE is fixed
    const directRoles =
      intervention.isCustom && providerTypeManifest
        ? mapRoleCodesToLabels(intervention.roles, providerTypeManifest)
        : intervention.roles;

    const unwrappedRoles =
      intervention.roleGroups &&
      intervention.roleGroups.length > 0 &&
      providerTypeManifest
        ? unwrapRoleGroups(intervention.roleGroups, providerTypeManifest).map(
            (pt) => pt.label,
          )
        : [];
    return [...directRoles, ...unwrappedRoles];
  };

  const isInterventionAlreadyAccounted = (
    intervention: CarePlanInterventionResponse,
    interventionList: CarePlanInterventionResponse[],
  ): boolean => {
    return interventionList.some(
      (existingIntervention) => existingIntervention.id === intervention.id,
    );
  };

  const setInterventionByPhaseStageLabel = (
    intervention: CarePlanInterventionResponse,
  ) => {
    const combinedRoles = getCombinedRoles(intervention);

    if (
      onlyIncludeRoles.length > 0 &&
      !combinedRoles.some((role) => onlyIncludeRoles.includes(role))
    ) {
      return;
    }

    if (intervention.isCustom) {
      if (
        includeCustomInterventions &&
        !isInterventionAlreadyAccounted(intervention, customInterventions)
      ) {
        customInterventions.push(intervention);
      }
      return;
    }

    // Non-custom interventions require a modality context. Ensure we do NOT surface non-custom interventions
    // even if one happens to carry an orphaned `treatmentPhaseStage.label`.
    if (!carePlan.treatmentEpisodeModality) {
      return;
    }

    const phaseStageLabel = intervention.treatmentPhaseStage?.label;
    if (!phaseStageLabel) {
      return;
    }

    if (!interventionsByPhaseStage[phaseStageLabel]) {
      interventionsByPhaseStage[phaseStageLabel] = [];
    }
    if (
      !isInterventionAlreadyAccounted(
        intervention,
        interventionsByPhaseStage[phaseStageLabel],
      )
    ) {
      interventionsByPhaseStage[phaseStageLabel].push(intervention);
    }
  };

  carePlan.problems.forEach((problem) => {
    problem.goals.forEach((goal) => {
      goal.interventions.forEach(setInterventionByPhaseStageLabel);
    });
  });

  carePlan.foundationalInterventions.forEach(setInterventionByPhaseStageLabel);

  return { interventionsByPhaseStage, customInterventions, isLoading: false };
};
